/**
 * Privacy Policy Page
 *
 * Comprehensive privacy policy for GRAutomates app that meets:
 * - Google Play Store requirements
 * - Apple App Store requirements
 * - GDPR compliance
 * - Australian Privacy Principles (APP)
 */

import { useState, useEffect } from 'react';
import { useCurrentUser } from '../../contexts/UserContext';
import { TEXT } from '../shared/Typography';

interface PrivacyPolicyProps {
  isAuthenticated?: boolean;
}

export default function PrivacyPolicy({ isAuthenticated = false }: PrivacyPolicyProps) {
  const effectiveDate = '14 January 2025';
  const lastUpdated = '14 January 2025';

  // Check for stored user on mount to determine auth state
  const _currentUser = useCurrentUser();
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (parsed?.token) {
          setIsLoggedIn(true);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  const handleBack = () => {
    if (isLoggedIn) {
      window.location.href = '/';
    } else {
      window.location.href = 'https://grautomates.grps.com.au';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Go back"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="h-8 w-px bg-gray-200" />
              <img
                src="/grautomates-dark.png"
                alt="GRAutomates"
                className="h-8 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <article className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Title Section */}
          <div className="px-6 sm:px-8 py-8 border-b border-gray-200 bg-white">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-gray-600">GRAutomates Application</p>
            <div className={`mt-4 flex flex-wrap gap-4 ${TEXT.helper}`}>
              <span>Effective Date: {effectiveDate}</span>
              <span>Last Updated: {lastUpdated}</span>
            </div>
          </div>

          {/* Policy Content */}
          <div className="px-6 sm:px-8 py-8 prose prose-gray max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                GR Production Services Pty Ltd (&quot;GRPS&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operates the
                GRAutomates application (the &quot;App&quot;). This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you use our App.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                GRAutomates is an enterprise resource planning (ERP) system designed for internal business
                operations including job management, procurement, financial management, human resources,
                mechanical operations, training compliance, and roster management.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using the App, you consent to the data practices described in this policy. If you do not
                agree with the terms of this Privacy Policy, please do not access or use the App.
              </p>
            </section>

            {/* Information We Collect */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>2. Information We Collect</h2>

              <h3 className={`mt-6 mb-3 ${TEXT.sectionTitle}`}>2.1 Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We collect personal information that you provide directly to us, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Account Information:</strong> Name, email address, employee ID, and job title</li>
                <li><strong>Authentication Data:</strong> Login credentials (processed through Azure Active Directory)</li>
                <li><strong>Employment Information:</strong> Person code, department, role, and organisational hierarchy</li>
                <li><strong>Contact Information:</strong> Work phone number and business address</li>
              </ul>

              <h3 className={`mt-6 mb-3 ${TEXT.sectionTitle}`}>2.2 Financial and Business Data</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                In the course of using the App for business operations, we process:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Expense Information:</strong> Receipt images, expense amounts, vendor details, and descriptions</li>
                <li><strong>Job and Project Data:</strong> Job codes, work centre assignments, and activity codes</li>
                <li><strong>Procurement Data:</strong> Purchase requisitions, vendor information, and approval workflows</li>
                <li><strong>Timesheet and Roster Data:</strong> Work hours, shift assignments, and attendance records</li>
                <li><strong>Training Records:</strong> Competency certifications, training completions, and compliance status</li>
              </ul>

              <h3 className={`mt-6 mb-3 ${TEXT.sectionTitle}`}>2.3 Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                When you use our App, we may automatically collect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Device Information:</strong> Device type, operating system, and unique device identifiers</li>
                <li><strong>Usage Data:</strong> Features accessed, actions taken, and timestamps</li>
                <li><strong>Log Data:</strong> Error reports, diagnostic data, and performance metrics</li>
                <li><strong>Network Information:</strong> Connection type and network status (for offline functionality)</li>
              </ul>

              <h3 className={`mt-6 mb-3 ${TEXT.sectionTitle}`}>2.4 Camera and Photo Access</h3>
              <p className="text-gray-700 leading-relaxed">
                The App may request access to your device&apos;s camera and photo library for the purpose of
                capturing and uploading receipt images for expense claims. This access is only used when
                you explicitly choose to take a photo or upload an image. We do not access your camera or
                photos without your direct action.
              </p>
            </section>

            {/* How We Use Your Information */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>3. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Service Delivery:</strong> To provide, maintain, and improve the App&apos;s functionality</li>
                <li><strong>Authentication:</strong> To verify your identity and manage your account access</li>
                <li><strong>Business Operations:</strong> To process expense claims, manage jobs, and facilitate procurement</li>
                <li><strong>Compliance:</strong> To maintain training records and ensure regulatory compliance</li>
                <li><strong>Communication:</strong> To send work-related notifications, approvals, and alerts</li>
                <li><strong>Integration:</strong> To synchronise data with enterprise systems (GreenTree ERP, LearnUpon LMS)</li>
                <li><strong>Analytics:</strong> To understand usage patterns and improve the App</li>
                <li><strong>Security:</strong> To detect, prevent, and address technical issues and security threats</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
              </ul>
            </section>

            {/* Data Sharing and Disclosure */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>4. Data Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share
                your information in the following circumstances:
              </p>

              <h3 className={`mt-6 mb-3 ${TEXT.sectionTitle}`}>4.1 Service Providers</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We engage trusted third-party service providers to assist in operating our App:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                <li><strong>Microsoft Azure:</strong> Authentication services (Azure Active Directory) and cloud infrastructure</li>
                <li><strong>Amazon Web Services (AWS):</strong> Document storage (S3) and optical character recognition (Textract) for receipt processing</li>
                <li><strong>GreenTree:</strong> Enterprise resource planning integration</li>
                <li><strong>LearnUpon:</strong> Learning management system for training records</li>
              </ul>

              <h3 className={`mt-6 mb-3 ${TEXT.sectionTitle}`}>4.2 Within the Organisation</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your information may be shared with authorised personnel within GR Production Services
                who require access for legitimate business purposes, including managers, administrators,
                and HR personnel.
              </p>

              <h3 className={`mt-6 mb-3 ${TEXT.sectionTitle}`}>4.3 Legal Requirements</h3>
              <p className="text-gray-700 leading-relaxed">
                We may disclose your information if required by law, regulation, legal process, or
                governmental request, or to protect the rights, property, or safety of GRPS, our
                employees, or others.
              </p>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>5. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organisational measures to protect your personal
                information, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Encryption:</strong> All data transmitted between the App and our servers is encrypted using TLS/SSL</li>
                <li><strong>Access Controls:</strong> Role-based access controls limit data access to authorised personnel</li>
                <li><strong>Authentication:</strong> Secure authentication through Azure Active Directory with multi-factor authentication support</li>
                <li><strong>Monitoring:</strong> Regular security monitoring and logging of system access</li>
                <li><strong>Infrastructure:</strong> Hosted on secure, enterprise-grade cloud infrastructure</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                While we strive to use commercially acceptable means to protect your personal information,
                no method of transmission over the internet or electronic storage is 100% secure. We cannot
                guarantee absolute security.
              </p>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>6. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your personal information for as long as necessary to fulfil the purposes outlined
                in this Privacy Policy, unless a longer retention period is required or permitted by law.
                Retention periods are determined by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Legal and regulatory requirements (e.g., financial records, employment records)</li>
                <li>Business operational needs</li>
                <li>Contractual obligations</li>
                <li>Resolution of disputes or enforcement of agreements</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                When your personal information is no longer required, we will securely delete or anonymise it.
              </p>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>7. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Subject to applicable law, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information in certain circumstances</li>
                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                <li><strong>Data Portability:</strong> Request transfer of your data in a structured format</li>
                <li><strong>Objection:</strong> Object to certain processing of your personal information</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us using the details provided in Section 12.
                Note that as an enterprise application, some requests may need to be processed through
                your employer&apos;s HR or IT department.
              </p>
            </section>

            {/* Children's Privacy */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>8. Children&apos;s Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                GRAutomates is an enterprise business application not intended for use by children under
                the age of 18. We do not knowingly collect personal information from children. The App is
                designed for use by employees and authorised business users of GR Production Services only.
                If you believe we have inadvertently collected information from a child, please contact us
                immediately.
              </p>
            </section>

            {/* Third-Party Services */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>9. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The App integrates with third-party services that have their own privacy policies:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Microsoft Azure / Office 365:</strong>{' '}
                  <a href="https://privacy.microsoft.com/en-us/privacystatement"
                     className="text-blue-600 hover:text-blue-800 underline"
                     target="_blank"
                     rel="noopener noreferrer">
                    Microsoft Privacy Statement
                  </a>
                </li>
                <li>
                  <strong>Amazon Web Services:</strong>{' '}
                  <a href="https://aws.amazon.com/privacy/"
                     className="text-blue-600 hover:text-blue-800 underline"
                     target="_blank"
                     rel="noopener noreferrer">
                    AWS Privacy Notice
                  </a>
                </li>
                <li>
                  <strong>Google Play Services (Android):</strong>{' '}
                  <a href="https://policies.google.com/privacy"
                     className="text-blue-600 hover:text-blue-800 underline"
                     target="_blank"
                     rel="noopener noreferrer">
                    Google Privacy Policy
                  </a>
                </li>
                <li>
                  <strong>Apple Services (iOS):</strong>{' '}
                  <a href="https://www.apple.com/legal/privacy/"
                     className="text-blue-600 hover:text-blue-800 underline"
                     target="_blank"
                     rel="noopener noreferrer">
                    Apple Privacy Policy
                  </a>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We encourage you to review the privacy policies of these third-party services.
              </p>
            </section>

            {/* International Data Transfers */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>10. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than Australia
                where our service providers operate. These countries may have different data protection
                laws. When we transfer your information internationally, we take appropriate steps to
                ensure your information remains protected in accordance with this Privacy Policy and
                applicable data protection laws.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices
                or for legal, operational, or regulatory reasons. We will notify you of any material
                changes by posting the updated policy in the App and updating the &quot;Last Updated&quot; date.
                Your continued use of the App after any changes constitutes your acceptance of the
                updated Privacy Policy.
              </p>
            </section>

            {/* Contact Us */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>12. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data
                practices, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <p className={`mb-3 ${TEXT.emphasis}`}>GR Production Services Pty Ltd</p>
                <p className="text-gray-700 mb-2">
                  <strong>Primary Contact:</strong> Kurt Zalmstra<br />
                  <span className="ml-4">Email:{' '}
                    <a href="mailto:kurt.zalmstra@grps.com.au" className="text-blue-600 hover:text-blue-800">
                      kurt.zalmstra@grps.com.au
                    </a>
                  </span><br />
                  <span className="ml-4">Phone: +61 437 287 454</span>
                </p>
                <p className="text-gray-700">
                  <strong>Secondary Contact:</strong> Stephen Tanevski<br />
                  <span className="ml-4">Email:{' '}
                    <a href="mailto:stephen.tanevski@grps.com.au" className="text-blue-600 hover:text-blue-800">
                      stephen.tanevski@grps.com.au
                    </a>
                  </span>
                </p>
              </div>
            </section>

            {/* Australian Privacy Principles */}
            <section className="mb-8">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>13. Australian Privacy Principles</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As an Australian organisation, we are committed to handling your personal information in
                accordance with the Australian Privacy Principles (APPs) contained in the Privacy Act 1988 (Cth).
                You may lodge a complaint with the Office of the Australian Information Commissioner (OAIC)
                if you believe we have breached the APPs.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Office of the Australian Information Commissioner</strong><br />
                Website:{' '}
                <a href="https://www.oaic.gov.au"
                   className="text-blue-600 hover:text-blue-800 underline"
                   target="_blank"
                   rel="noopener noreferrer">
                  www.oaic.gov.au
                </a><br />
                Phone: 1300 363 992
              </p>
            </section>

            {/* Consent */}
            <section className="mb-4">
              <h2 className={`mb-4 ${TEXT.titleLg}`}>14. Consent</h2>
              <p className="text-gray-700 leading-relaxed">
                By using the GRAutomates application, you acknowledge that you have read, understood, and
                agree to be bound by this Privacy Policy. If you are using the App on behalf of an
                organisation, you represent that you have the authority to bind that organisation to this
                Privacy Policy.
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="px-6 sm:px-8 py-6 bg-gray-50 border-t border-gray-200">
            <p className={`text-center ${TEXT.helper}`}>
              &copy; {new Date().getFullYear()} GR Production Services Pty Ltd. All rights reserved.
            </p>
          </div>
        </article>

      </main>

      {/* Page Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className={TEXT.helper}>
              &copy; {new Date().getFullYear()} GR Production Services Pty Ltd
            </p>
            <div className={`flex items-center gap-6 ${TEXT.helper}`}>
              <a href="https://grps.com.au" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
                grps.com.au
              </a>
              <span>ABN: 95 098 080 626</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
